/**
 * Email Service
 * 
 * Handles sending transactional emails for sessions, cycles, and meetings.
 * Uses a pluggable provider interface to support different email services.
 * 
 * Supports:
 * - Resend (default)
 * - SendGrid
 * - SMTP (generic)
 * - Console/log (development)
 */

// ============================================================================
// Types
// ============================================================================

export type EmailProvider = 'resend' | 'sendgrid' | 'smtp' | 'console';

export interface EmailConfig {
  provider: EmailProvider;
  apiKey?: string;
  fromEmail: string;
  fromName: string;
  replyTo?: string;
  
  // App URL for links in emails
  appBaseUrl?: string;
  
  // SMTP-specific
  smtpHost?: string;
  smtpPort?: number;
  smtpUser?: string;
  smtpPass?: string;
  smtpSecure?: boolean;
}

export interface EmailRecipient {
  email: string;
  name?: string;
}

export interface EmailMessage {
  to: EmailRecipient[];
  cc?: EmailRecipient[];
  bcc?: EmailRecipient[];
  subject: string;
  text?: string;
  html: string;
  replyTo?: string;
  tags?: string[];
  metadata?: Record<string, string>;
}

export interface EmailResult {
  success: boolean;
  messageId?: string;
  error?: string;
}

export type EmailTemplateType = 
  | 'session_invitation'
  | 'session_reminder'
  | 'session_cancelled'
  | 'session_results'
  | 'cycle_phase_change'
  | 'meeting_invitation'
  | 'meeting_reminder'
  | 'meeting_cancelled'
  | 'welcome'
  | 'custom';

// ============================================================================
// Configuration
// ============================================================================

let emailConfig: EmailConfig = {
  provider: 'console',
  fromEmail: 'notifications@peakpriorities.com',
  fromName: 'Peak Priorities',
  appBaseUrl: 'http://localhost:3150',
};

/**
 * Configure the email service
 */
export function configureEmailService(config: Partial<EmailConfig>): void {
  emailConfig = { ...emailConfig, ...config };
}

/**
 * Get current email configuration
 */
export function getEmailConfig(): EmailConfig {
  return { ...emailConfig };
}

/**
 * Initialize email service from environment variables
 * Call this at app startup to configure based on env
 */
export function initializeEmailService(): void {
  // Check for Resend API key
  const resendApiKey = import.meta.env?.VITE_RESEND_API_KEY;
  if (resendApiKey) {
    console.log('[EmailService] Configuring Resend as email provider');
    configureEmailService({
      provider: 'resend',
      apiKey: resendApiKey,
    });
  }
  
  // Check for SendGrid API key as fallback
  const sendgridApiKey = import.meta.env?.VITE_SENDGRID_API_KEY;
  if (!resendApiKey && sendgridApiKey) {
    console.log('[EmailService] Configuring SendGrid as email provider');
    configureEmailService({
      provider: 'sendgrid',
      apiKey: sendgridApiKey,
    });
  }
  
  // Configure from email if provided
  const fromEmail = import.meta.env?.VITE_EMAIL_FROM_ADDRESS;
  if (fromEmail) {
    configureEmailService({ fromEmail });
  }
  
  const fromName = import.meta.env?.VITE_EMAIL_FROM_NAME;
  if (fromName) {
    configureEmailService({ fromName });
  }
  
  // Configure app base URL
  const appBaseUrl = import.meta.env?.VITE_APP_BASE_URL || window?.location?.origin;
  if (appBaseUrl) {
    configureEmailService({ appBaseUrl });
  }
  
  console.log(`[EmailService] Initialized with provider: ${emailConfig.provider}`);
}

// ============================================================================
// Email Sending
// ============================================================================

/**
 * Send an email
 */
export async function sendEmail(message: EmailMessage): Promise<EmailResult> {
  try {
    switch (emailConfig.provider) {
      case 'resend':
        return await sendViaResend(message);
      case 'sendgrid':
        return await sendViaSendGrid(message);
      case 'smtp':
        return await sendViaSMTP(message);
      case 'console':
      default:
        return await sendViaConsole(message);
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error sending email',
    };
  }
}

/**
 * Send via Resend
 */
async function sendViaResend(message: EmailMessage): Promise<EmailResult> {
  if (!emailConfig.apiKey) {
    throw new Error('Resend API key not configured');
  }
  
  const response = await fetch('https://api.resend.com/emails', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${emailConfig.apiKey}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      from: `${emailConfig.fromName} <${emailConfig.fromEmail}>`,
      to: message.to.map(r => r.email),
      cc: message.cc?.map(r => r.email),
      bcc: message.bcc?.map(r => r.email),
      subject: message.subject,
      html: message.html,
      text: message.text,
      reply_to: message.replyTo || emailConfig.replyTo,
      tags: message.tags?.map(t => ({ name: t, value: 'true' })),
    }),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Resend API error');
  }
  
  const result = await response.json();
  return {
    success: true,
    messageId: result.id,
  };
}

/**
 * Send via SendGrid
 */
async function sendViaSendGrid(message: EmailMessage): Promise<EmailResult> {
  if (!emailConfig.apiKey) {
    throw new Error('SendGrid API key not configured');
  }
  
  const response = await fetch('https://api.sendgrid.com/v3/mail/send', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${emailConfig.apiKey}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      personalizations: [{
        to: message.to.map(r => ({ email: r.email, name: r.name })),
        cc: message.cc?.map(r => ({ email: r.email, name: r.name })),
        bcc: message.bcc?.map(r => ({ email: r.email, name: r.name })),
      }],
      from: { email: emailConfig.fromEmail, name: emailConfig.fromName },
      reply_to: message.replyTo ? { email: message.replyTo } : undefined,
      subject: message.subject,
      content: [
        { type: 'text/plain', value: message.text || '' },
        { type: 'text/html', value: message.html },
      ],
      categories: message.tags,
    }),
  });
  
  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || 'SendGrid API error');
  }
  
  return {
    success: true,
    messageId: response.headers.get('x-message-id') || undefined,
  };
}

/**
 * Send via SMTP (placeholder - would need nodemailer in Node environment)
 */
async function sendViaSMTP(message: EmailMessage): Promise<EmailResult> {
  console.log('[EmailService] SMTP sending not implemented in browser environment');
  console.log('[EmailService] Would send:', {
    to: message.to,
    subject: message.subject,
  });
  
  return {
    success: true,
    messageId: `smtp-${Date.now()}`,
  };
}

/**
 * Send via Console (development/testing)
 */
async function sendViaConsole(message: EmailMessage): Promise<EmailResult> {
  console.log('═══════════════════════════════════════════════════════════════');
  console.log('[EmailService] Sending email (console mode):');
  console.log('───────────────────────────────────────────────────────────────');
  console.log(`From: ${emailConfig.fromName} <${emailConfig.fromEmail}>`);
  console.log(`To: ${message.to.map(r => r.email).join(', ')}`);
  if (message.cc?.length) console.log(`Cc: ${message.cc.map(r => r.email).join(', ')}`);
  console.log(`Subject: ${message.subject}`);
  console.log('───────────────────────────────────────────────────────────────');
  console.log(message.text || stripHtml(message.html));
  console.log('═══════════════════════════════════════════════════════════════');
  
  return {
    success: true,
    messageId: `console-${Date.now()}`,
  };
}

// ============================================================================
// Email Templates
// ============================================================================

/**
 * Generate session invitation email
 */
export function generateSessionInvitationEmail(params: {
  sessionTitle: string;
  sessionDescription?: string;
  scheduledStart: Date;
  duration: number;
  facilitatorName: string;
  meetingLink?: string;
  sessionUrl: string;
}): { subject: string; html: string; text: string } {
  const dateStr = formatDate(params.scheduledStart);
  const timeStr = formatTime(params.scheduledStart);
  
  const subject = `You're invited: ${params.sessionTitle}`;
  
  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%); color: white; padding: 30px; border-radius: 12px 12px 0 0; }
    .content { background: #f9fafb; padding: 30px; border: 1px solid #e5e7eb; border-top: none; border-radius: 0 0 12px 12px; }
    .button { display: inline-block; background: #6366f1; color: white; padding: 12px 24px; border-radius: 8px; text-decoration: none; margin: 10px 0; }
    .details { background: white; padding: 20px; border-radius: 8px; margin: 20px 0; }
    .detail-row { display: flex; margin: 8px 0; }
    .detail-label { font-weight: 600; width: 100px; color: #6b7280; }
    .footer { text-align: center; padding: 20px; color: #6b7280; font-size: 12px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1 style="margin: 0; font-size: 24px;">Session Invitation</h1>
      <p style="margin: 10px 0 0 0; opacity: 0.9;">You've been invited to participate</p>
    </div>
    <div class="content">
      <h2 style="margin-top: 0;">${escapeHtml(params.sessionTitle)}</h2>
      ${params.sessionDescription ? `<p>${escapeHtml(params.sessionDescription)}</p>` : ''}
      
      <div class="details">
        <div class="detail-row">
          <span class="detail-label">Date:</span>
          <span>${dateStr}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Time:</span>
          <span>${timeStr}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Duration:</span>
          <span>${params.duration} minutes</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Facilitator:</span>
          <span>${escapeHtml(params.facilitatorName)}</span>
        </div>
      </div>
      
      <a href="${params.sessionUrl}" class="button">View Session Details</a>
      ${params.meetingLink ? `<a href="${params.meetingLink}" class="button" style="background: #059669;">Join Meeting</a>` : ''}
    </div>
    <div class="footer">
      <p>This email was sent by Peak Priorities</p>
      <p>If you don't want to receive these emails, you can <a href="#">manage your preferences</a></p>
    </div>
  </div>
</body>
</html>
  `.trim();
  
  const text = `
SESSION INVITATION

${params.sessionTitle}
${params.sessionDescription || ''}

Date: ${dateStr}
Time: ${timeStr}
Duration: ${params.duration} minutes
Facilitator: ${params.facilitatorName}

View Session: ${params.sessionUrl}
${params.meetingLink ? `Join Meeting: ${params.meetingLink}` : ''}

---
This email was sent by Peak Priorities
  `.trim();
  
  return { subject, html, text };
}

/**
 * Generate session reminder email
 */
export function generateSessionReminderEmail(params: {
  sessionTitle: string;
  scheduledStart: Date;
  minutesUntil: number;
  meetingLink?: string;
  sessionUrl: string;
}): { subject: string; html: string; text: string } {
  const timeStr = formatTime(params.scheduledStart);
  const reminderText = params.minutesUntil >= 60 
    ? `${Math.floor(params.minutesUntil / 60)} hour${params.minutesUntil >= 120 ? 's' : ''}`
    : `${params.minutesUntil} minutes`;
  
  const subject = `Reminder: "${params.sessionTitle}" starts in ${reminderText}`;
  
  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: #f59e0b; color: white; padding: 30px; border-radius: 12px 12px 0 0; text-align: center; }
    .content { background: #f9fafb; padding: 30px; border: 1px solid #e5e7eb; border-top: none; border-radius: 0 0 12px 12px; text-align: center; }
    .button { display: inline-block; background: #6366f1; color: white; padding: 12px 24px; border-radius: 8px; text-decoration: none; margin: 10px 5px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1 style="margin: 0; font-size: 24px;">⏰ Session Reminder</h1>
      <p style="margin: 10px 0 0 0; font-size: 18px;">Starting in ${reminderText}</p>
    </div>
    <div class="content">
      <h2 style="margin-top: 0;">${escapeHtml(params.sessionTitle)}</h2>
      <p style="font-size: 18px; color: #6b7280;">Scheduled for ${timeStr}</p>
      
      <div style="margin: 30px 0;">
        <a href="${params.sessionUrl}" class="button">View Session</a>
        ${params.meetingLink ? `<a href="${params.meetingLink}" class="button" style="background: #059669;">Join Now</a>` : ''}
      </div>
    </div>
  </div>
</body>
</html>
  `.trim();
  
  const text = `
SESSION REMINDER

"${params.sessionTitle}" starts in ${reminderText}

Time: ${timeStr}

View Session: ${params.sessionUrl}
${params.meetingLink ? `Join Now: ${params.meetingLink}` : ''}
  `.trim();
  
  return { subject, html, text };
}

/**
 * Generate cycle phase change email
 */
export function generateCyclePhaseChangeEmail(params: {
  cycleName: string;
  previousPhase: string;
  newPhase: string;
  phaseDescription: string;
  cycleUrl: string;
}): { subject: string; html: string; text: string } {
  const subject = `Cycle Update: "${params.cycleName}" moved to ${params.newPhase}`;
  
  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: linear-gradient(135deg, #059669 0%, #10b981 100%); color: white; padding: 30px; border-radius: 12px 12px 0 0; }
    .content { background: #f9fafb; padding: 30px; border: 1px solid #e5e7eb; border-top: none; border-radius: 0 0 12px 12px; }
    .phase-transition { display: flex; align-items: center; justify-content: center; gap: 20px; margin: 20px 0; padding: 20px; background: white; border-radius: 8px; }
    .phase { padding: 10px 20px; border-radius: 6px; font-weight: 600; }
    .phase-old { background: #e5e7eb; color: #6b7280; }
    .phase-new { background: #dcfce7; color: #059669; }
    .arrow { font-size: 24px; color: #9ca3af; }
    .button { display: inline-block; background: #6366f1; color: white; padding: 12px 24px; border-radius: 8px; text-decoration: none; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1 style="margin: 0; font-size: 24px;">Phase Transition</h1>
      <p style="margin: 10px 0 0 0; opacity: 0.9;">${escapeHtml(params.cycleName)}</p>
    </div>
    <div class="content">
      <div class="phase-transition">
        <span class="phase phase-old">${escapeHtml(params.previousPhase)}</span>
        <span class="arrow">→</span>
        <span class="phase phase-new">${escapeHtml(params.newPhase)}</span>
      </div>
      
      <p>${escapeHtml(params.phaseDescription)}</p>
      
      <div style="text-align: center; margin-top: 30px;">
        <a href="${params.cycleUrl}" class="button">View Cycle Details</a>
      </div>
    </div>
  </div>
</body>
</html>
  `.trim();
  
  const text = `
CYCLE PHASE TRANSITION

${params.cycleName}

${params.previousPhase} → ${params.newPhase}

${params.phaseDescription}

View Cycle: ${params.cycleUrl}
  `.trim();
  
  return { subject, html, text };
}

/**
 * Generate meeting invitation email
 */
export function generateMeetingInvitationEmail(params: {
  meetingTitle: string;
  meetingDescription?: string;
  meetingType: string;
  scheduledStart: Date;
  duration: number;
  organizerName: string;
  meetingLink?: string;
  location?: string;
  agenda?: string[];
  meetingUrl: string;
}): { subject: string; html: string; text: string } {
  const dateStr = formatDate(params.scheduledStart);
  const timeStr = formatTime(params.scheduledStart);
  
  const subject = `Meeting Invitation: ${params.meetingTitle}`;
  
  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%); color: white; padding: 30px; border-radius: 12px 12px 0 0; }
    .content { background: #f9fafb; padding: 30px; border: 1px solid #e5e7eb; border-top: none; border-radius: 0 0 12px 12px; }
    .button { display: inline-block; background: #6366f1; color: white; padding: 12px 24px; border-radius: 8px; text-decoration: none; margin: 10px 5px; }
    .details { background: white; padding: 20px; border-radius: 8px; margin: 20px 0; }
    .agenda { background: white; padding: 20px; border-radius: 8px; margin: 20px 0; }
    .agenda-item { padding: 8px 0; border-bottom: 1px solid #e5e7eb; }
    .agenda-item:last-child { border-bottom: none; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1 style="margin: 0; font-size: 24px;">Meeting Invitation</h1>
      <p style="margin: 10px 0 0 0; opacity: 0.9;">${escapeHtml(params.meetingType)}</p>
    </div>
    <div class="content">
      <h2 style="margin-top: 0;">${escapeHtml(params.meetingTitle)}</h2>
      ${params.meetingDescription ? `<p>${escapeHtml(params.meetingDescription)}</p>` : ''}
      
      <div class="details">
        <p><strong>📅 Date:</strong> ${dateStr}</p>
        <p><strong>🕐 Time:</strong> ${timeStr}</p>
        <p><strong>⏱️ Duration:</strong> ${params.duration} minutes</p>
        <p><strong>👤 Organizer:</strong> ${escapeHtml(params.organizerName)}</p>
        ${params.location ? `<p><strong>📍 Location:</strong> ${escapeHtml(params.location)}</p>` : ''}
      </div>
      
      ${params.agenda && params.agenda.length > 0 ? `
        <div class="agenda">
          <h3 style="margin-top: 0;">Agenda</h3>
          ${params.agenda.map(item => `<div class="agenda-item">• ${escapeHtml(item)}</div>`).join('')}
        </div>
      ` : ''}
      
      <div style="text-align: center;">
        <a href="${params.meetingUrl}" class="button">View Details</a>
        ${params.meetingLink ? `<a href="${params.meetingLink}" class="button" style="background: #059669;">Join Meeting</a>` : ''}
      </div>
    </div>
  </div>
</body>
</html>
  `.trim();
  
  const text = `
MEETING INVITATION

${params.meetingTitle}
${params.meetingType}
${params.meetingDescription || ''}

Date: ${dateStr}
Time: ${timeStr}
Duration: ${params.duration} minutes
Organizer: ${params.organizerName}
${params.location ? `Location: ${params.location}` : ''}

${params.agenda && params.agenda.length > 0 ? `
Agenda:
${params.agenda.map(item => `• ${item}`).join('\n')}
` : ''}

View Details: ${params.meetingUrl}
${params.meetingLink ? `Join Meeting: ${params.meetingLink}` : ''}
  `.trim();
  
  return { subject, html, text };
}

// ============================================================================
// Task Assignment Email
// ============================================================================

/**
 * Send task assignment email to a participant
 * 
 * @param params.recipientEmail - If provided, sends to this email. Otherwise uses placeholder.
 */
export async function sendTaskAssignmentEmail(params: {
  recipientId: string;
  recipientEmail?: string;
  recipientName?: string;
  taskId: string;
  taskName: string;
  toolName: string;
  deadline?: Date;
  cycleId: string;
  facilitatorId: string;
}): Promise<EmailResult> {
  const { subject, html, text } = generateTaskAssignmentEmail(params);
  
  // Use provided email, or fall back to placeholder for development
  const recipientEmail = params.recipientEmail || `user-${params.recipientId}@example.com`;
  
  if (!params.recipientEmail) {
    console.warn(`[EmailService] No email address for user ${params.recipientId}, using placeholder`);
  }
  
  const result = await sendEmail({
    to: [{ email: recipientEmail, name: params.recipientName }],
    subject,
    html,
    text,
    tags: ['task-assignment', `task-${params.taskId}`],
  });
  
  if (result.success) {
    console.log(`[EmailService] Sent task assignment email to ${recipientEmail} for task ${params.taskId}`);
  } else {
    console.error(`[EmailService] Failed to send email to ${recipientEmail}: ${result.error}`);
  }
  
  return result;
}

/**
 * Generate task assignment email content
 */
export function generateTaskAssignmentEmail(params: {
  taskId: string;
  taskName: string;
  toolName: string;
  deadline?: Date;
  cycleId: string;
  facilitatorId: string;
}): { subject: string; html: string; text: string } {
  const deadlineStr = params.deadline ? formatDate(params.deadline) : 'No deadline set';
  const taskUrl = `${emailConfig.appBaseUrl}/prioritization/my-work`;
  
  const subject = `Action Required: ${params.taskName}`;
  
  const toolDisplayName = getToolDisplayName(params.toolName);
  
  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%); color: white; padding: 30px; border-radius: 12px 12px 0 0; }
    .content { background: #f9fafb; padding: 30px; border: 1px solid #e5e7eb; border-top: none; border-radius: 0 0 12px 12px; }
    .button { display: inline-block; background: #6366f1; color: white; padding: 12px 24px; border-radius: 8px; text-decoration: none; margin: 10px 0; }
    .details { background: white; padding: 20px; border-radius: 8px; margin: 20px 0; }
    .deadline { color: #dc2626; font-weight: bold; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1 style="margin: 0; font-size: 24px;">New Prioritization Task</h1>
      <p style="margin: 10px 0 0 0; opacity: 0.9;">You've been invited to participate</p>
    </div>
    <div class="content">
      <h2 style="margin-top: 0;">${escapeHtml(params.taskName)}</h2>
      
      <p>You have been assigned a prioritization task that requires your input.</p>
      
      <div class="details">
        <p><strong>🎯 Task:</strong> ${escapeHtml(params.taskName)}</p>
        <p><strong>🛠️ Method:</strong> ${escapeHtml(toolDisplayName)}</p>
        <p><strong>📅 Deadline:</strong> <span class="deadline">${deadlineStr}</span></p>
      </div>
      
      <p>Please complete your prioritization before the deadline to ensure your input is included in the final results.</p>
      
      <div style="text-align: center;">
        <a href="${taskUrl}" class="button">Start Task</a>
      </div>
      
      <p style="color: #666; font-size: 14px; margin-top: 20px;">
        If you have any questions, please contact your facilitator.
      </p>
    </div>
  </div>
</body>
</html>
  `.trim();
  
  const text = `
NEW PRIORITIZATION TASK

You have been assigned a prioritization task that requires your input.

Task: ${params.taskName}
Method: ${toolDisplayName}
Deadline: ${deadlineStr}

Please complete your prioritization before the deadline to ensure your input is included in the final results.

Start Task: ${taskUrl}

If you have any questions, please contact your facilitator.
  `.trim();
  
  return { subject, html, text };
}

/**
 * Get display name for a tool ID
 */
function getToolDisplayName(toolId: string): string {
  const toolNames: Record<string, string> = {
    'stack-ranking': 'Stack Ranking',
    'dot-voting': 'Dot Voting',
    'buy-a-feature': 'Buy a Feature',
    'pairwise-comparison': 'Pairwise Comparison',
    'rice': 'RICE Scoring',
    'moscow': 'MoSCoW',
    'wsjf': 'WSJF',
    'impact-effort': 'Impact vs Effort',
    'ahp': 'Analytic Hierarchy Process',
    'max-priorities-pyramid': 'Max Priorities Pyramid',
    'fast-vs-right': 'Fast vs Right',
  };
  return toolNames[toolId] || toolId;
}

// ============================================================================
// Utility Functions
// ============================================================================

function formatDate(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
}

function formatTime(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  }).format(date);
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function stripHtml(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

export default {
  configureEmailService,
  getEmailConfig,
  sendEmail,
  generateSessionInvitationEmail,
  generateSessionReminderEmail,
  generateCyclePhaseChangeEmail,
  generateMeetingInvitationEmail,
  sendTaskAssignmentEmail,
  generateTaskAssignmentEmail,
};
